import React, { useState } from 'react';
import { X } from 'lucide-react';
import { createFlatRateTemplate } from '../lib/flatRateTemplates';
import { useBusiness } from '../contexts/BusinessContext';
import toast from 'react-hot-toast';

interface FlatRateHoursModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceId: string;
  onSuccess?: () => void;
}

export default function FlatRateHoursModal({
  isOpen,
  onClose,
  serviceId,
  onSuccess
}: FlatRateHoursModalProps) {
  const { selectedBusiness } = useBusiness();
  const [title, setTitle] = useState('');
  const [hours, setHours] = useState<number>(0);
  const [ratePerHour, setRatePerHour] = useState<number | undefined>();
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setSaving(true);
      await createFlatRateTemplate({
        business_id: selectedBusiness.id,
        service_id: serviceId,
        title,
        hours,
        rate_per_hour: ratePerHour,
        description: description || undefined
      });

      toast.success('Template created successfully');
      onSuccess?.();
      onClose();
      
      // Reset form
      setTitle('');
      setHours(0);
      setRatePerHour(undefined);
      setDescription('');
    } catch (err) {
      console.error('Error creating template:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const isValid = title.trim() !== '' && hours > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Overlay */}
        <div className="fixed inset-0 bg-black/30" onClick={onClose} />

        {/* Modal */}
        <div className="relative w-full max-w-md rounded-lg bg-white shadow-lg">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-lg font-medium">Flat Rate Hours Template</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                placeholder="Enter template title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hours <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={hours}
                onChange={(e) => setHours(parseFloat(e.target.value) || 0)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rate per Hour
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={ratePerHour || ''}
                  onChange={(e) => setRatePerHour(e.target.value ? parseFloat(e.target.value) : undefined)}
                  className="w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                placeholder="Optional description"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-4 py-3 flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={!isValid || saving}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Apply Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 